import fetchWrapper from "../../../fetchWrapper";

// Constantes para tipos de filtros de fecha
const DATE_FILTER_TYPES = [
  "expires_in",
  "created",
  "created_at",
  "updated_at",
  "latest_link_created_at",
  "first_entry",
  "last_exit",
];

const ERROR_MESSAGES = {
  RATE_LIMIT:
    "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.",
  SERVER_ERROR: "Error del servidor al realizar la solicitud",
};

/**
 * Verifica si un tipo de filtro es de fecha
 * @param {string} filterType - Tipo de filtro a verificar
 * @returns {boolean} - true si es un filtro de fecha
 */
const isDateFilterType = (filterType) => DATE_FILTER_TYPES.includes(filterType);

/**
 * Construye los parámetros de URL para filtros
 * @param {Array} filters - Arreglo de filtros a aplicar
 * @returns {string} - Cadena de parámetros de URL
 */
const buildFilterParams = (filters) => {
  if (!filters || !Array.isArray(filters)) return "";

  return filters.reduce((params, filter) => {
    if (isDateFilterType(filter.type)) {
      return (
        params +
        `&type_date[]=${encodeURIComponent(filter.type)}` +
        `&dateFrom[]=${encodeURIComponent(filter.value.from)}` +
        `&dateUntil[]=${encodeURIComponent(filter.value.until)}`
      );
    }
    return (
      params +
      `&type_filter[]=${encodeURIComponent(filter.type)}` +
      `&search[]=${encodeURIComponent(filter.value)}`
    );
  }, "");
};

/**
 * Maneja la respuesta de error de límite de peticiones (429)
 * @returns {Object} - Objeto con status y mensaje de error
 */
const handleRateLimitError = () => ({
  status: false,
  message: ERROR_MESSAGES.RATE_LIMIT,
});

/**
 * Procesa la respuesta exitosa del servidor
 * @param {Object} result - Resultado de la petición
 * @returns {Object} - Datos de paginación y éxito
 */
const processSuccessResponse = (result) => {
  const {
    payload: { data, last_page, total, from, to },
  } = result;
  return { data, last_page, total, from, to, success: result.success };
};

/**
 * Procesa la respuesta de error del servidor
 * @param {Object} result - Resultado de la petición
 * @returns {Object} - Objeto con status y mensaje de error
 */
const processErrorResponse = (result) => ({
  status: false,
  message: result.message || ERROR_MESSAGES.SERVER_ERROR,
});

/**
 * Obtiene datos filtrados de una tabla específica
 * @param {number} pageIndex - Índice de la página actual
 * @param {number} pageSize - Tamaño de la página
 * @param {Array} filters - Arreglo de filtros a aplicar
 * @param {string} Table - Nombre de la tabla a consultar
 * @returns {Promise<Object>} - Datos filtrados con información de paginación
 */
const getPerFilter = async (pageIndex, pageSize, filters, Table) => {
  try {
    const baseUrl = `/${Table}/${Table}_per_filter?page=${pageIndex}&pageSize=${pageSize}`;
    const filterParams = buildFilterParams(filters);
    const url = baseUrl + filterParams;

    const response = await fetchWrapper(process.env.REACT_APP_API_URL + url);

    if (response.status === 429) {
      return handleRateLimitError();
    }

    const result = await response.json();

    if (result.success === true) {
      return processSuccessResponse(result);
    }

    return processErrorResponse(result);
  } catch (error) {
    return {
      status: false,
      message: error.message || ERROR_MESSAGES.SERVER_ERROR,
    };
  }
};

/**
 * Obtiene datos filtrados por archivo o fuente de datos
 * @param {string|number} id - ID del archivo o fuente de datos
 * @param {number} pageIndex - Índice de la página actual
 * @param {number} pageSize - Tamaño de la página
 * @param {Array} filters - Arreglo de filtros a aplicar
 * @param {string} Table - Nombre de la tabla a consultar
 * @returns {Promise<Object>} - Datos filtrados con información de paginación
 */
const getPerFilterPerFile = async (id, pageIndex, pageSize, filters, Table) => {
  try {
    const idParam = Table === "cards" ? "file_id" : "per_data_source";
    const baseUrl = `/${Table}/${Table}_per_filter_${idParam}/${id}?page=${pageIndex}&pageSize=${pageSize}`;
    const filterParams = buildFilterParams(filters);
    const url = baseUrl + filterParams;

    const response = await fetchWrapper(process.env.REACT_APP_API_URL + url);

    if (response.status === 429) {
      return handleRateLimitError();
    }

    const result = await response.json();

    if (result.success === true) {
      return processSuccessResponse(result);
    }

    return processErrorResponse(result);
  } catch (error) {
    return {
      status: false,
      message: error.message || ERROR_MESSAGES.SERVER_ERROR,
    };
  }
};

/**
 * Convierte el valor del filtro de asistencia a booleano
 * @param {string} value - Valor del filtro de asistencia
 * @returns {string|null} - "true", "false" o null si no es válido
 */
const parseAttendedValue = (value) => {
  const attendedValues = ["Asistieron", "true", "1"];
  const notAttendedValues = ["No Asistieron", "false", "0"];

  if (attendedValues.includes(value)) return "true";
  if (notAttendedValues.includes(value)) return "false";
  return null;
};

/**
 * Construye los parámetros de URL para filtros de reportes de asistencia
 * @param {Array} filters - Arreglo de filtros a aplicar
 * @returns {string} - Cadena de parámetros de URL
 */
const buildReportFilterParams = (filters) => {
  if (!filters || !Array.isArray(filters)) return "";

  return filters.reduce((params, filter) => {
    switch (filter.type) {
      case "name":
      case "number_id":
      case "email":
      case "card_type":
      case "total_reads_min":
      case "total_reads_max":
        return params + `&${filter.type}=${encodeURIComponent(filter.value)}`;

      case "attended": {
        const attendedValue = parseAttendedValue(filter.value);
        return attendedValue ? params + `&attended=${attendedValue}` : params;
      }

      case "first_entry":
      case "last_exit":
        if (filter.value?.from && filter.value?.until) {
          return (
            params +
            `&${filter.type}_from=${encodeURIComponent(filter.value.from)}` +
            `&${filter.type}_until=${encodeURIComponent(filter.value.until)}`
          );
        }
        return params;

      default:
        return params;
    }
  }, "");
};

/**
 * Procesa la respuesta exitosa de reportes de asistencia
 * @param {Object} result - Resultado de la petición
 * @returns {Object} - Datos con información de paginación
 */
const processReportSuccessResponse = (result) => {
  const { pagination } = result;
  return {
    data: result,
    last_page: pagination?.last_page || 1,
    total: pagination?.total || 0,
    from: pagination?.from || 0,
    to: pagination?.to || 0,
    success: result.success,
  };
};

/**
 * Obtiene reportes de asistencia filtrados
 * @param {string|number} id - ID del evento
 * @param {number} pageIndex - Índice de la página actual
 * @param {number} pageSize - Tamaño de la página
 * @param {Array} filters - Arreglo de filtros a aplicar
 * @returns {Promise<Object>} - Datos de reportes con información de paginación
 */
const getReportsPerFilter = async (id, pageIndex, pageSize, filters) => {
  try {
    const baseUrl = `/cards/event_attendance/${id}?page=${pageIndex}&pageSize=${pageSize}`;
    const filterParams = buildReportFilterParams(filters);
    const url = baseUrl + filterParams;

    const response = await fetchWrapper(process.env.REACT_APP_API_URL + url);

    if (response.status === 429) {
      return handleRateLimitError();
    }

    const result = await response.json();

    if (result.success === true) {
      return processReportSuccessResponse(result);
    }

    return processErrorResponse(result);
  } catch (error) {
    return {
      status: false,
      message: error.message || ERROR_MESSAGES.SERVER_ERROR,
    };
  }
};

export { getPerFilter, getPerFilterPerFile, getReportsPerFilter };
